import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../api';

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [isValidToken, setIsValidToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');

  useEffect(() => {
    // Verify the token on mount
    const verifyToken = async () => {
      try {
        const res = await api.get(`/auth/verify-reset-token/${token}`);
        setIsValidToken(true);
        setTokenEmail(res.data.email || '');
      } catch (err) {
        setIsValidToken(false);
      }
    };

    if (token) {
      verifyToken();
    }
  }, [token]);

  const validateForm = () => {
    const errors = {};

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const res = await api.post('/auth/reset-password', {
        token,
        newPassword: formData.password
      });

      setIsSuccess(true);

      // Auto-redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/super-admin/login');
      }, 3000);
    } catch (err) {
      setServerError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (isValidToken === null) {
    return (
      <div className="login-page" style={styles.page}>
        <div className="login-card" style={styles.card}>
          <div style={styles.logoSection}>
            <div style={styles.logo}>⏳</div>
            <h1 style={styles.title}>Verifying...</h1>
            <p style={styles.subtitle}>Please wait while we verify your reset link</p>
          </div>
        </div>
      </div>
    );
  }

  if (isValidToken === false) {
    return (
      <div className="login-page" style={styles.page}>
        <div className="login-card" style={styles.card}>
          <div style={styles.logoSection}>
            <div style={styles.logo}>❌</div>
            <h1 style={styles.title}>Invalid Link</h1>
            <p style={styles.subtitle}>This password reset link has expired or is invalid</p>
          </div>

          <div style={styles.footer}>
            <Link to="/super-admin/login" style={styles.link}>
              Go to Login
            </Link>
          </div>

          <div style={styles.backLink}>
            <a href="/login" style={styles.link}>← Back to Main Login</a>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="login-page" style={styles.page}>
        <div className="login-card" style={styles.card}>
          <div style={styles.logoSection}>
            <div style={styles.logo}>✅</div>
            <h1 style={styles.title}>Password Reset</h1>
            <p style={styles.subtitle}>Your password has been successfully reset</p>
          </div>

          <div style={styles.successMessage}>
            Redirecting to login page in a few seconds...
          </div>

          <div style={styles.footer}>
            <Link to="/super-admin/login" style={styles.link}>
              Click here to login now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" style={styles.page}>
      <div className="login-card" style={styles.card}>
        <div style={styles.logoSection}>
          <div style={styles.logo}>🔑</div>
          <h1 style={styles.title}>Set New Password</h1>
          <p style={styles.subtitle}>Enter your new password for {tokenEmail}</p>
        </div>

        {serverError && <div style={styles.error}>{serverError}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>New Password *</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Minimum 8 characters"
              style={validationErrors.password ? { ...styles.input, borderColor: '#dc2626' } : styles.input}
            />
            {validationErrors.password && <span style={styles.fieldError}>{validationErrors.password}</span>}
          </div>

          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Confirm New Password *</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              style={validationErrors.confirmPassword ? { ...styles.input, borderColor: '#dc2626' } : styles.input}
            />
            {validationErrors.confirmPassword && <span style={styles.fieldError}>{validationErrors.confirmPassword}</span>}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={styles.button}
          >
            {isLoading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div style={styles.footer}>
          <Link to="/super-admin/login" style={styles.link}>
            ← Back to Login
          </Link>
        </div>

        <div style={styles.backLink}>
          <a href="/login" style={styles.link}>← Back to Main Login</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logo: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#6b7280',
    fontSize: '14px',
    margin: 0,
  },
  error: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  successMessage: {
    background: '#dcfce7',
    color: '#15803d',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  input: {
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    transition: 'border-color 0.2s',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  fieldError: {
    color: '#dc2626',
    fontSize: '12px',
  },
  button: {
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'opacity 0.2s',
  },
  footer: {
    marginTop: '24px',
    textAlign: 'center',
  },
  link: {
    color: '#667eea',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '600',
  },
  backLink: {
    marginTop: '16px',
    textAlign: 'center',
  },
};

export default ResetPassword;